import { Colorpicker, ColorpickerEvents } from "@dhx/ts-colorpicker";
import { awaitRedraw, el, inject, create } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate, locateNode, toNode } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, toDataDriver, toProxy } from "@dhx/ts-data";
import { Grid, GridEvents, ICol } from "@dhx/ts-grid";
import { Layout } from "@dhx/ts-layout";
import { Toolbar } from "@dhx/ts-toolbar";
import { ContextMenu, Menu } from "@dhx/ts-menu";
import { ActionsManager } from "./actions/ActionsManager";
import { getContextMenuStruct, getSheetsContextMenuStruct } from "./configs/context";
import { getMenuStruct } from "./configs/menu";
import { getToolbarStruct } from "./configs/toolbar";
import { EditLine } from "./EditLine";
import { getHeaderCell, getIndexRowCell } from "./helpers/actions";
import { BufferManager } from "./helpers/buffer";
import { CustomCsvDriver } from "./helpers/CustomCsvDriver";
import {
	detectCellFormat,
	getCleanValue,
	getDefaultFormatsMap,
	getFormat,
	getFormattedValue,
	initFormat,
	isNumeric,
} from "./helpers/format";
import {
	convertStyles,
	doesContainUrl,
	extendConfig,
	getCellIds,
	getCellIndex,
	getCellInfo,
	getCellNameById,
	getCellsArray,
	getLetterFromNumber,
	getRangeIndexes,
	getRangeMatrix,
	isRangeId,
	isWasmSupported,
	getStylesObject,
	checkSpan,
	isCellSpaned,
	showAlert,
} from "./helpers/main";
import { getAutoFilledCells, getCellsDiff } from "./helpers/selection";
import { getToggledValue, updateToolbar } from "./helpers/toolbar";
import { KeyManager, IKeyManager } from "@dhx/ts-common/newKeyManager";
import locale from "./locales/en";
import { Selection } from "./Selection";
import { DateEditor } from "./editors/DateEditor";
import {
	Actions,
	FileFormat,
	IActionsManager,
	IBufferManager,
	ICellInfo,
	Id,
	IDataWithStyles,
	IExecuteConfig,
	IFormats,
	ISelection,
	ISheet,
	ISpreadsheet,
	ISpreadsheetConfig,
	IStylesList,
	SpreadsheetEvents,
	IEditor,
	ICellMeta,
	ILink,
	IActionConfig,
	IPageMeta,
	IFilterRules,
	IFilter,
	ISpan,
} from "./types";
import { Popup, PopupEvents } from "../../ts-popup";
import { NavigationBarEvents } from "@dhx/ts-navbar";

import { findIndex, rgbToHex, isDefined } from "@dhx/ts-common/core";
import { Exporter } from "./Exporter";
import { XlsxDriver } from "./helpers/XlsxDriver";
import { FileProxy } from "./helpers/FileProxy";
import { initMathStore, Store } from "./helpers/math";
import { DataPage, DataStore, IFormula, toId } from "@xbs/muon";
import { IWindow, WindowEvents } from "../../ts-window/sources/types";
import { Window } from "@dhx/ts-window";
import { getKeysHandlers } from "./helpers/hotkeys";
import { focusManager } from "@dhx/ts-common/FocusManager";
import {
	excelDateToJSDate,
	getExcelDateFromJs,
	getExcelDateFromString,
	timeStringToJS,
} from "./helpers/date";
import { stringToDate } from "@dhx/ts-common/date";
import { SelectEditor } from "./editors/SelectEditor";
import { TimeEditor } from "./editors/TimeEditor";
import { ValidationForm } from "./forms/ValidationForm";
import { SearchForm } from "./forms/SearchForm";
import { Position, tooltip } from "@dhx/ts-message";
import { LinkForm } from "./forms/LinkForm";
import { FilterForm } from "./forms/FilterForm";
import { Merge } from "./forms/Merge";
import { normalizeSpan } from "@dhx/ts-grid/sources/ui/Cells";

declare const dhx: any;

export class Spreadsheet extends View implements ISpreadsheet {
	public container: HTMLElement;
	public events: IEventSystem<SpreadsheetEvents>;
	public selection: ISelection;
	public config: ISpreadsheetConfig;
	public toolbar: Toolbar;
	public menu: Menu;
	public contextMenu: ContextMenu;
	public sheetContextMenu: ContextMenu;
	public export: Exporter;
	public keyManager: IKeyManager;

	private _editLine: EditLine;
	private _dataStore: DataStore;
	private _math: Store;
	private _grid: Grid;
	private _layout: Layout;
	private _tabbar: Toolbar;
	private _colorpicker: Colorpicker;
	private _colorpickerPopup: Popup;
	private _colorpickerTarget: string;
	private _actionsManager: IActionsManager;
	private _sizes: { rowsCount: number; colsCount: number };
	private _buffer: IBufferManager;
	private _activeInput: HTMLInputElement;
	private _xlsxProxy: FileProxy;
	private _silencedColorChange: boolean;
	private _editors: { [key: string]: IEditor };
	private _range: {
		cell: string;
		mode: number;
		cursor: number;
		inline: number;
		editor: HTMLInputElement;
	};
	private _sheets: DataCollection;
	private _activePage: DataPage;
	private _focusedSheet: string;
	private _errorAlert: IWindow;
	private _editMode: { page: DataPage; cell: string; pageName: string };
	private _activeSheetName: ISheet;
	private _validationForm: ValidationForm;
	private _searchForm: SearchForm;
	private _linkForm: LinkForm;
	private _contextMenuVisible: boolean;
	private _filterForm: FilterForm;
	private _spanControl: Merge;

	constructor(container: HTMLElement | string, config: ISpreadsheetConfig) {
		super(container, config);
		this.container = container === null ? (container as any) : toNode(container);
		this._sizes = {
			rowsCount: 1000,
			colsCount: 200,
		};
		this._editors = {};
		this.config = extendConfig(
			{
				rowsCount: { validate: val => val >= 0, default: 1000 },
				colsCount: { validate: val => val >= 0, default: 26 },
				menu: { validate: val => typeof val === "boolean", default: false },
				editLine: {
					validate: val => typeof val === "boolean",
					default: true,
				},
				readonly: {
					validate: val => typeof val === "boolean",
					default: false,
				},
				autoFormat: {
					validate: val => typeof val === "boolean",
					default: true,
				},

				importModulePath: {
					validate: val => typeof val === "string",
					default: "https://cdn.dhtmlx.com/libs/excel2json/1.1/worker.js",
				},
				exportModulePath: {
					validate: val => typeof val === "string",
					default: "https://cdn.dhtmlx.com/libs/json2excel/1.0/worker.js",
				},
				multiSheets: { validate: val => typeof val === "boolean", default: true },
				dateFormat: { validate: val => typeof val === "string", default: "%d/%m/%Y" },
				timeFormat: { validate: val => typeof val === "number", default: 12 },

				formats: { validate: val => val instanceof Array, default: [] },
			},
			this.config
		);

		this._range = { cell: "", mode: 0, cursor: 0, editor: null, inline: 0 };

		this.events = new EventSystem();
		initFormat(this, this.config.dateFormat, this.config.timeFormat);

		this._initLayout();

		this._colorpicker = new Colorpicker(null);
		this._colorpickerPopup = new Popup();
		this._colorpickerPopup.attach(this._colorpicker);
		this._linkForm = new LinkForm(this._grid, this.insertLink.bind(this), this.events);

		this._setEventsHandlers();

		this._actionsManager = new ActionsManager({
			spreadsheet: this,
			editLine: this._editLine,
			grid: this._grid,
			dataStore: this._dataStore,
			events: this.events,
		});

		this._buffer = new BufferManager(
			this,
			this._grid,
			// eslint-disable-next-line @typescript-eslint/unbound-method
			this._callAction,
			this._activePage,
			this._dataStore
		);

		dhx.css.add(
			{
				textDecoration: "underline",
				color: "#163FA2",
			},
			"dhx_spreadsheet_link"
		);

		this.selection = new Selection(this, this._grid, this._buffer, this._activePage);

		const gridRoot = this._grid.getRootView();
		gridRoot.hooks = gridRoot.hooks || {};
		gridRoot.hooks.didMount = vm => {
			this._updateGridSizes();
			const cell = this.selection.getFocusedCell();
			const val = this.getValue(cell);
			this._editLine.setValue(val);
			if (!this.config.readonly) {
				vm.node.el.addEventListener("contextmenu", (e: MouseEvent) => {
					this.contextMenu.data.update("lock", {
						value: this.isLocked(this.selection.getSelectedCell())
							? locale.unlockCell
							: locale.lockCell,
					});
					this._contextMenuVisible = true;
					this.contextMenu.showAt(e);
					e.preventDefault();
				});
			}

			// [todo] move to layout didMount event
			this.container = this._layout.getRootView().node.el;

			gridRoot.hooks.didRedraw = view => {
				this.events.fire(SpreadsheetEvents.gridRedraw, [view]);
			};
		};
		this._initHotkeys();

		const tabbarRoot = this._tabbar.getRootView();
		tabbarRoot.hooks = tabbarRoot.hooks || {};
		const addContextListener = vm => {
			vm.node.el.addEventListener("contextmenu", e => {
				this.endEdit();
				const id = locate(e);
				if (id && id !== "add-sheet" && !this.config.readonly) {
					const sheetsLength = this._sheets.getLength();
					const contextData = this.sheetContextMenu.data;
					if (this.config.multiSheets && sheetsLength === 2) {
						contextData.update("delete", { disabled: true });
					} else if (
						this.config.multiSheets &&
						sheetsLength > 2 &&
						contextData.getItem("delete").disabled
					) {
						contextData.update("delete", { disabled: false });
					}
					this.sheetContextMenu.showAt(e.target, "right");
					this._focusedSheet = id;
					e.preventDefault();
				}
			});
		};
		tabbarRoot.hooks.didMount = vm => {
			vm.node &&
				vm.node.el &&
				vm.node.el.addEventListener("dblclick", e => {
					const id = locate(e);
					if (id && id !== "add-sheet" && !this.config.readonly) {
						e.preventDefault();
						this._focusedSheet = id;
						this.sheetContextMenu.events.fire(NavigationBarEvents.click, ["rename", e]);
						awaitRedraw().then(() => {
							focusManager.setFocusId(null);
						});
					}
				});
			addContextListener(vm);
		};
		tabbarRoot.hooks.didRedraw = vm => addContextListener(vm);

		this._generateGridStruct();

		this.export = new Exporter(this, this._grid);

		const view = create({ render: () => this._render() });
		this.mount(container, view);
		focusManager.setFocusId(this._uid);
		this._spanControl = new Merge(
			this._callAction.bind(this),
			this.config,
			this._grid,
			this.isLocked.bind(this)
		);
		this._filterForm = new FilterForm(this._grid, this.setFilter.bind(this));
	}

	destructor() {
		this.unmount();
		this._layout.unmount();
		this.contextMenu.destructor();
		this.menu.destructor();
		this.toolbar.destructor();
		this._editLine.destructor();
		this._tabbar.destructor();
		this._searchForm?.destructor();
		this._linkForm?.destructor();
	}

	paint() {
		if (
			this.config.rowsCount !== this._sizes.rowsCount ||
			this.config.colsCount !== this._sizes.colsCount
		) {
			this._generateGridStruct();
		}
		this._layout.paint();
		this._grid.paint();
	}

	load(url: string, type?: FileFormat): Promise<any> {
		if (type === "xlsx" || (type === "json" && !url)) {
			if (!isWasmSupported()) {
				throw new Error("WebAssembly is not supported by your browser");
			}
			this._xlsxProxy = this._xlsxProxy || new FileProxy(this);
			return this._xlsxProxy.load(type, url).then(data => {
				this.parse(data, type);
			});
		}
		const proxy = toProxy(url);
		return proxy.load<any>().then(data => {
			this.parse(data, type);
		});
	}

	parse(data: IDataWithStyles | ICellInfo[], type?: any) {
		if (type === "csv") {
			type = new CustomCsvDriver(this._grid);
		}
		if (type === "xlsx") {
			type = new XlsxDriver();
		}
		this._sheets.forEach(sheet => {
			if (sheet.id !== "add-sheet") {
				this._dataStore.removePage(sheet.value);
				this._sheets.remove(sheet.id);
			}
		});

		const driver = toDataDriver(type || "json");
		data = (driver as any).toJsonArray(data, this._grid) as IDataWithStyles;

		this._grid.config.columns.map(col => {
			col.$cellCss = {};
		});

		const preparedData: any = {
			sheets: [],
		};

		const { multiSheets, formats, autoFormat, leftSplit = -1, topSplit = -1 } = this.config;

		if ("sheets" in data) {
			for (let i = 0; i < (multiSheets ? data.sheets.length : 1); i++) {
				const sheet = data.sheets[i];
				sheet.name = sheet.name || this._generateNewSheetName();
				preparedData.sheets.push(sheet);
			}
		} else {
			if ("data" in data) {
				preparedData.sheets.push({
					data: data.data,
					name: this._activeSheetName.name,
				});
			}
		}

		if ("styles" in data) {
			const styles = data.styles;
			for (const key in styles) {
				const css = styles[key];
				dhx.css.add({ ...convertStyles(css), "white-space": "nowrap" }, key);
			}
			dhx.css.update();
		}

		if (Array.isArray(data)) {
			preparedData.sheets.push({ data, name: this._generateNewSheetName() });
		}

		for (const sheet of preparedData.sheets) {
			this._dataStore.addPage(sheet.name);
			this._sheets.add({ value: sheet.name, id: sheet.id });
		}

		for (const sheet of preparedData.sheets) {
			const page = this._dataStore.getPage(sheet.name);
			let initData = [...sheet.data];
			let sheetData: any[] = [];
			while (initData.length) {
				sheetData.push(initData.splice(0, 1000));
			}

			sheetData.forEach((lazyData, index) => {
				if (!index) {
					this._parseCells(lazyData, page);
				} else {
					setTimeout(() => this._parseCells(lazyData, page), 100);
				}
			});

			const rowHeight = this._grid.config.rowHeight;
			const cols =
				sheet.cols ||
				this._grid.config.columns.slice(1).map(col => {
					return { width: col.width };
				});
			const rows =
				sheet.rows ||
				this._grid.data.map(() => {
					return { height: rowHeight };
				});
			const [rowsCounts, colsCounts] = page.getSize();
			while (rows.length < rowsCounts) {
				rows.push({ height: rowHeight });
			}
			while (cols.length < colsCounts) {
				cols.push({ width: 120 });
			}
			let merged = sheet.merged || [];
			if ((topSplit || leftSplit) && merged) {
				merged = merged.filter((span: ISpan) => checkSpan(span, leftSplit, topSplit, this._grid));
				merged = merged.map((span: ISpan) => {
					const { row: toRow, column: toColumn } = span.to;
					return {
						...span,
						to: {
							row: toRow > rowsCounts ? this._grid.data.getLength() - 1 : toRow,
							column: toColumn > colsCounts ? this._grid.config.columns?.length - 2 : toColumn,
						},
					};
				});
			}
			page.setMeta({
				cols,
				rows,
				merged,
			});
		}

		this._grid.data.events.fire(DataEvents.change);
		this._changeSheet(this._sheets.getId(this.config.multiSheets && !this.config.readonly ? 1 : 0));
		this._grid.paint();
		if (this.config && !this.config.readonly) {
			this.selection.setSelectedCell("A1");
		}
	}

	_parseCells(data, page: DataPage) {
		const { formats = [], autoFormat } = this.config;
		for (const item of data) {
			const { row, col } = getCellIds(item.cell.toUpperCase(), this._grid);
			const clean = item.value ? getCleanValue(item.value) : "";
			const isNumber = isNumeric(clean);
			let value: any;
			let format: IFormats;
			let css = item.css || "";
			if (clean.startsWith("http") && clean.split(" ").length === 1) {
				css = `dhx_spreadsheet_link ${css}`;
			}

			const isCorrectFormat =
				item.format === "General"
					? formats.find(i => i.id === "common")
					: formats.find(i => i.id === item.format || i.mask === item.format);
			format = isCorrectFormat || formats.find(i => i.mask === detectCellFormat(item.value));
			item.format = format.mask;

			if (isNumber && format?.mask !== "@") {
				if (autoFormat && format.mask.includes("%")) {
					value =
						item.value == clean
							? parseFloat(clean).toFixed(4)
							: +(parseFloat(clean) / 100).toFixed(4);
				} else {
					value = +clean;
				}
			} else {
				value = item.value;
			}
			const formatId = format.id;
			if ((formatId === "date" || formatId === "time") && value) {
				if (isNumeric(value)) {
					value = +value;
				} else if (value instanceof Date) {
					value = getExcelDateFromJs(value);
				} else {
					value = getExcelDateFromString(value, format);
				}
			}
			if (item.css instanceof Array) {
				item.css = item.css.join(" ");
			}
			// [FIXME] can be optimized (later)
			// instead of checking triggers for each cell
			// we can force recalck of all formulas after full data loading
			// so things like SUM(A1:A100) will be calculated once, not 100 times
			page.setValue(row, col, value, format.mask === "@" ? 1 : 0);
			const href = doesContainUrl(value);
			const link = href ? { href } : null;

			page.setCell(row, col, { format, css: item.css, editor: item.editor, link });
		}
	}

	serialize() {
		const styles = {};
		const sheets = [];

		this._sheets.forEach(item => {
			if (item.id === "add-sheet") {
				return;
			}
			const sheet = item.value;
			const page = this._dataStore.getPage(sheet);
			const { cols = [], rows = [], merged = [] } = page.getMeta();
			const [pageRows, pageCols] = this._activePage.getSize();
			const serializedData = page.serialize(true);
			const preparedItem = {
				name: sheet,
				data: [],
				cols: cols.slice(0, pageCols),
				rows: rows.slice(0, pageRows).map(r => ({ height: r.height })),
				merged,
			};
			for (const item of serializedData) {
				const cell = getCellNameById(this._grid, (item[0] + 1).toString(), (item[1] + 1).toString());
				const cellMeta = item[2];

				if (cellMeta) {
					const { value, css, format, editor } = cellMeta;

					const cellObj = { cell } as any;
					if (css) {
						cellObj.css = css;
						styles[cellObj.css] = dhx.css.get(cellObj.css);
					}
					if (editor) {
						cellObj.editor = editor;
					}
					if (format !== undefined) {
						const formatObj = getFormat(getDefaultFormatsMap()[format.mask]);
						cellObj.format = formatObj?.id || format.id;
					}
					if (value || typeof value === "number") {
						cellObj.value = value;
					}
					if (cell && (cellObj.value || typeof cellObj.value === "number" || css || editor)) {
						preparedItem.data.push(cellObj);
					}
				}
			}
			sheets.push(preparedItem);
		});

		return {
			sheets,
			styles,
			formats: this.config.formats,
		};
	}

	setValue(cell: string, value: any | any[]) {
		if (!cell) {
			return;
		}

		this._checkForMissedCells(cell);

		if (!this.events.fire(SpreadsheetEvents.beforeValueChange, [cell, value])) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		const id = getCellIds(cleanCell, this._grid);
		this._callAction({
			row: id.row,
			col: id.col,
			cell,
			val: value,
			action: Actions.setCellValue,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			page,
		});
		this._grid.paint();
	}

	getValue(cell: string): any | any[] {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		if (isRangeId(cell)) {
			const values = [];
			this.eachCell(_c => {
				const id = getCellIds(_c, this._grid);
				values.push(page.getValue(id.row, id.col));
			}, cell);
			return values;
		}

		const id = getCellIds(cell, this._grid);
		const val = page.getValue(id.row, id.col);
		return val;
	}

	getCellIndex(cell: string) {
		return getCellIds(cell, this._grid);
	}

	getFormula(cell: string): string | string[] {
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		if (isRangeId(cell)) {
			const formulas = [];
			this.eachCell(_c => {
				formulas.push(this._getMath(_c, page) || "");
			}, cell);
			return formulas;
		}
		const formula = this._getMath(cell, page);
		return formula ? formula.source : "";
	}

	getSheets(): ISheet[] {
		const sheets = [];
		this._sheets.forEach(item => {
			if (item.id !== "add-sheet") {
				sheets.push({ name: item.value, id: item.id });
			}
		});
		return sheets;
	}

	_getMath(cell: string, page = this._activePage): IFormula {
		if (!cell) {
			return;
		}
		const { row, col } = getCellIds(cell, this._grid);
		const cellId = toId(row, col, page.getID());
		return this._math.getMath(cellId) || null;
	}

	eachCell(cb: any, range?: string) {
		range = range || this.selection.getSelectedCell();

		const cells = getCellsArray(range, this._grid);
		for (const cell of cells) {
			const { row, col } = getCellIds(cell, this._grid);
			const val = this._activePage.getValue(row, col);
			cb(cell, val);
		}
	}

	getStyle(cell: string): IStylesList | IStylesList[] {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		if (isRangeId(cell)) {
			const styles = [];
			this.eachCell(c => {
				const info = getCellInfo(c, page, this._grid);
				let css = [];
				if (info.css) {
					css = typeof info.css === "string" ? info.css.split(" ") : [info.css];
				}
				let customCssStyles = {};
				for (const classnName of css) {
					customCssStyles = { ...customCssStyles, ...dhx.css.get(classnName) };
				}
				styles.push(customCssStyles);
			}, cell);
			return styles;
		}

		const info = getCellInfo(cell, this._activePage, this._grid);
		let css = [];
		if (info.css) {
			css = typeof info.css === "string" ? info.css.split(" ") : [info.css];
		}
		let customCssStyles = {};
		for (const styles of css) {
			customCssStyles = { ...customCssStyles, ...dhx.css.get(styles) };
		}
		return customCssStyles;
	}

	setStyle(cell: string, style: string | string[] | IStylesList | IStylesList[]) {
		if (!cell || !style) {
			return;
		}
		if (!this.events.fire(SpreadsheetEvents.beforeStyleChange, [cell, style])) {
			return;
		}
		const { row, col } = getCellIds(cell, this._grid);

		if (!Object.keys(style).length) {
			this._callAction({
				row,
				col,
				cell,
				action: Actions.removeCellStyles,
				groupAction: Actions.groupAction,
				dataStore: this._dataStore,
			});
			return;
		}

		this._callAction({
			val: style,
			row,
			col,
			cell,
			action: Actions.setCellStyle,
			groupAction: Actions.groupAction,
		});
	}

	setFilter(cell?: string, filter?: IFilterRules[]) {
		if (cell && isRangeId(cell) && cell.includes(",")) {
			return;
		}

		this._callAction({
			val: filter,
			cell: cell ?? "A1",
			action: Actions.filter,
		});
	}

	getFilter(id?: string): IFilter {
		const sheet = this._getSheet(id) || this.getActiveSheet();
		const page = this._dataStore.getPage(sheet.name);
		if (!page) return;
		const { filter } = page.getMeta();
		return filter;
	}

	getFormat(cell: string) {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		if (isRangeId(cell)) {
			const formats = [];
			this.eachCell(c => {
				formats.push(getCellInfo(c, page, this._grid).format?.id || "common");
			}, cell);
			return formats;
		}
		const formatMask = getCellInfo(cell, page, this._grid).format?.mask;
		const formatAlias = getDefaultFormatsMap()[formatMask];

		return formatAlias || formatMask;
	}

	setFormat(cell: string, format: Id | Id[]) {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		this._callAction({
			val: format,
			cell,
			action: Actions.setCellFormat,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			page,
		});
	}
	clearSheet(id?: string) {
		const sheet = this._getSheet(id) || this.getActiveSheet();
		if (!this.events.fire(SpreadsheetEvents.beforeSheetClear, [sheet])) {
			return;
		}
		this._callAction({
			val: sheet.name,
			action: Actions.clearSheet,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterSheetClear, [sheet]);
	}

	clear() {
		if (!this.events.fire(SpreadsheetEvents.beforeClear, [])) {
			return;
		}
		this._callAction({
			action: Actions.clear,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterClear, []);
	}

	isLocked(cell: string): boolean {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		const id = getCellIds(cell, this._grid);

		if ("start" in id) {
			let locked = false;
			this.eachCell(cellId => {
				if (!locked) {
					const info = getCellInfo(cellId, page, this._grid).locked;
					locked = info;
				}
			}, cell);
			return locked;
		}
		const isLocked = getCellInfo(cell, page, this._grid).locked;

		return !!isLocked;
	}

	lock(cell: string) {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		const id = getCellIds(cell, this._grid);
		this._callAction({
			row: id.row,
			col: id.col,
			val: true,
			cell,
			action: Actions.lockCell,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			page,
		});
		this._grid.paint();
	}

	unlock(cell: string) {
		if (!cell) {
			return;
		}
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		const id = getCellIds(cell, this._grid);
		this._callAction({
			row: id.row,
			col: id.col,
			val: false,
			cell,
			action: Actions.lockCell,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			page,
		});
		this._grid.paint();
	}

	addRow(cell: string) {
		if (!this.events.fire(SpreadsheetEvents.beforeRowAdd, [cell])) {
			return;
		}
		if (isRangeId(cell)) {
			cell = cell.split(":")[0];
		}
		this._callAction({
			cell,
			action: Actions.addRow,
			groupAction: Actions.groupRowAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterRowAdd, [cell]);
	}

	deleteRow(cell: string) {
		if (!this.events.fire(SpreadsheetEvents.beforeRowDelete, [cell])) {
			return;
		}
		const id = getCellIds(cell, this._grid);
		this._callAction({
			row: id.row,
			col: id.col,
			cell,
			action: Actions.deleteRow,
			groupAction: Actions.groupRowAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterRowDelete, [cell]);
	}

	addColumn(cell: string) {
		if (!this.events.fire(SpreadsheetEvents.beforeColumnAdd, [cell])) {
			return;
		}
		if (isRangeId(cell)) {
			cell = cell.split(":")[0];
		}
		this._callAction({
			cell,
			action: Actions.addColumn,
			groupAction: Actions.groupColAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterColumnAdd, [cell]);
	}

	deleteColumn(cell: string) {
		if (!this.events.fire(SpreadsheetEvents.beforeColumnDelete, [cell])) {
			return;
		}

		this._callAction({
			cell,
			action: Actions.deleteColumn,
			groupAction: Actions.groupColAction,
			dataStore: this._dataStore,
		});
		this.events.fire(SpreadsheetEvents.afterColumnDelete, [cell]);
	}

	undo() {
		this._actionsManager.undo();
	}

	redo() {
		this._actionsManager.redo();
	}

	startEdit(cell?: string, initialValue?: string) {
		if (this.config.readonly || this._range.mode) {
			return;
		}
		if (this._editMode) {
			return this._editLine.setValueAtRedactor(initialValue);
		}
		const focused = this.selection.getFocusedCell();

		if (!cell) {
			cell = focused;
		}

		cell = cell.toUpperCase();
		this._checkForMissedCells(cell);

		if (!this.events.fire(SpreadsheetEvents.beforeEditStart, [cell, initialValue])) {
			return;
		}

		if (this.isLocked(cell)) {
			return;
		}

		if (focused !== cell) {
			this.endEdit(true);
			this.selection.setSelectedCell(cell);
		}
		const { edited, link } = getCellInfo(cell, this._activePage, this._grid);
		if (!this.selection.getSelectedCell()) this.selection.setSelectedCell(cell);

		if (link) {
			return this._showLinkPopup(cell);
		}

		if (!edited) {
			const { row, col } = getCellIds(cell, this._grid);
			const cellId = toId(row, col, this._activePage.getID());
			const math = this._math.getMath(cellId);
			if (math) initialValue = "=" + math.source;
			this._activePage.setCell(row, col, {
				edited: true,
				nextValue: initialValue,
				editorValue: initialValue,
			});

			if (initialValue) this._editLine.setValue(initialValue);

			this.events.fire(SpreadsheetEvents.afterEditStart, [cell, initialValue]);
		}
	}

	endEdit(withoutSave?: boolean) {
		const focused = this.selection.getFocusedCell();
		let { edited, nextValue, format, value } = getCellInfo(focused, this._activePage, this._grid);

		if (edited) {
			if (!this.events.fire(SpreadsheetEvents.beforeEditEnd, [focused, nextValue])) {
				return;
			}
			if (nextValue) {
				this._activePage.setMeta({ inserted: true });
			}

			if (!withoutSave && nextValue !== undefined) {
				if (format?.id === "time" && isNumeric(value)) {
					const nextDate = timeStringToJS(nextValue);
					if (nextDate instanceof Date) {
						const prevDate = excelDateToJSDate(value as number);
						prevDate.setHours(nextDate.getHours(), nextDate.getMinutes(), nextDate.getSeconds());
						nextValue = getExcelDateFromJs(prevDate);
					}
				}
				if (
					format?.id === "date" &&
					isNumeric(value) &&
					stringToDate(nextValue, this.config.dateFormat, true)
				) {
					const nextDate = stringToDate(nextValue, this.config.dateFormat);
					const prevDate = excelDateToJSDate(value as number);
					nextDate.setHours(prevDate.getHours(), prevDate.getMinutes(), prevDate.getSeconds());
					nextValue = getExcelDateFromJs(nextDate);
				}
				this.setValue(focused, nextValue);
			}
			const { row, col } = getCellIds(focused, this._grid);
			this._activePage.setCell(row, col, {
				nextValue: undefined,
				edited: false,
			});
			this._editLine.blur();
			this.events.fire(SpreadsheetEvents.afterEditEnd, [focused, nextValue]);
		}
	}

	addSheet(name?: string) {
		if (!this.config.multiSheets) return;
		let newSheet = name;
		if (!newSheet) {
			newSheet = this._generateNewSheetName();
		}
		if (!this.events.fire(SpreadsheetEvents.beforeSheetAdd, [newSheet])) {
			return;
		}
		this._callAction({
			val: newSheet,
			action: Actions.addSheet,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			sheets: this._sheets,
			prev: this._tabbar.getSelected()[0],
		});
		return this._sheets.find(item => item.value === newSheet).id;
	}

	removeSheet(id: string) {
		const item = this._getSheet(id);
		if (!item || !this.config.multiSheets || (this.config.multiSheets && this._sheets.getLength() === 2))
			return;
		if (!this.events.fire(SpreadsheetEvents.beforeSheetRemove, [item])) {
			return;
		}

		this._callAction({
			action: Actions.deleteSheet,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			sheets: this._sheets,
			prev: item.id,
			val: item.name,
		});
		this.events.fire(SpreadsheetEvents.afterSheetRemove, [item]);
	}

	getActiveSheet(): ISheet {
		return this._activeSheetName;
	}

	setActiveSheet(id: Id): void {
		this.endEdit();
		this._changeSheet(id);
	}

	setValidation(cell: string, options: string | string[]) {
		const { page, cleanCell } = this._parseCell(cell);
		if (!page) return;
		cell = cleanCell.toUpperCase();
		this._callAction({
			val: isRangeId(cell) ? [options] : options,
			cell,
			action: Actions.setValidation,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			page,
		});
	}

	search(text = "", openSearch = false, sheetId?: Id) {
		if (!this._searchForm) {
			this._searchForm = new SearchForm(this);
		}
		let page = this._activePage;
		const item = this._getSheet(sheetId as Id);
		if (isDefined(sheetId)) {
			if (!item) return;
			page = this._dataStore.getPage(item.name);
		}
		if (this._activePage !== page && item && openSearch) {
			this._changeSheet(item.id);
		}
		if (openSearch) {
			this._searchForm.show(page);
		}
		return this._searchForm.search(text, page);
	}

	hideSearch() {
		this._searchForm?.hide();
	}

	insertLink(cell: string, link?: ILink) {
		if (!cell) return;
		this._callAction({
			val: link,
			cell,
			action: Actions.insertLink,
			dataStore: this._dataStore,
			page: this._activePage,
		});
	}
	fitColumn(cell: string) {
		this._callAction({
			cell,
			action: Actions.fitColumn,
			dataStore: this._dataStore,
			page: this._activePage,
		});
	}

	sortCells(cell: string, dir = 1) {
		this._callAction({
			action: Actions.sortCells,
			groupAction: Actions.sortCells,
			dataStore: this._dataStore,
			cell,
			dir,
			page: this._activePage,
		});
	}

	mergeCells(cell: string, remove: boolean = false) {
		this._spanControl.doAction(cell, remove);
	}

	private _showLinkPopup(cell: string) {
		if (!cell || this._contextMenuVisible) return;
		this._linkForm.show(cell, getCellInfo(cell, this._activePage, this._grid));
	}

	private _showFilter(cell: string, e: MouseEvent) {
		e.stopPropagation();
		const { filter } = this._activePage.getMeta();
		this._filterForm.show(cell, e, filter, this._activePage);
	}

	private _checkValidation(cell) {
		const { editor, value } = getCellInfo(cell, this._activePage, this._grid);
		if (!editor) return true;
		let options: any = editor.options;
		if (typeof options === "string") {
			const { start, end, row, col } = getCellIds(options as string, this._grid);
			options = row
				? [this._activePage.getValue(row, col)]
				: this._activePage.getRange(start.row, start.col, end.row, end.col);
		}
		return (typeof value !== "number" && !value) || options.includes(value);
	}

	private _renameSheet(id: Id, newName: string) {
		const item = this._sheets.getItem(id);
		if (!item || !newName || item.prevValue === newName) return;
		if (
			!this.events.fire(SpreadsheetEvents.beforeSheetRename, [
				{ name: item.value, id: item.id },
				newName,
			])
		) {
			return;
		}
		this._callAction({
			val: newName,
			action: Actions.renameSheet,
			groupAction: Actions.groupAction,
			dataStore: this._dataStore,
			sheets: this._sheets,
			prev: id,
		});
		this.events.fire(SpreadsheetEvents.afterSheetRename, [{ name: item.value, id: item.id }]);
	}

	private _getSheet(id: Id) {
		const sheets = this.getSheets();
		return sheets.find(i => i.id === id);
	}

	private _changeSheet(id: Id) {
		const item = this._sheets.getItem(id);
		if (!item) return;
		if (!this.events.fire(SpreadsheetEvents.beforeSheetChange, [this._activeSheetName])) {
			return;
		}
		const prevPage = this._activePage;
		const scroll = this._grid.getScrollState();
		prevPage && prevPage.setMeta({ scroll });
		const prevSheet = this._activeSheetName ? this._activeSheetName.name : null;
		const pos = this._editLine.isCellExpected(
			"A1",
			false,
			this._range.inline ? this._range.editor || null : null
		);
		const focusedCell = this.selection && this.selection.getFocusedCell();
		const info = getCellInfo(focusedCell, this._activePage, this._grid);
		if (info && info.edited && !pos) {
			this.endEdit();
		}
		this._activePage = this._dataStore.getPage(item.value);
		this._activeSheetName = { name: item.value, id };
		this._tabbar.select(id);
		const pageMeta = this._activePage.getMeta() && (this._activePage.getMeta() as IPageMeta);
		if (this._activePage && !pageMeta.toolbarId) {
			this._activePage.setMeta({ toolbarId: id });
		}

		if ((pos && !this._editMode) || (pos && this._editMode.page !== this._activePage)) {
			this._activePage.setMeta({
				selectedCells: null,
				focusedCell: null,
			});
			this._editMode = this._editMode || {
				page: prevPage,
				cell: focusedCell,
				pageName: prevSheet,
			};
		} else if (pos && this._editMode && this._editMode.page === this._activePage) {
			const val = this._editLine.getValue();
			this._editMode = null;
			this._editLine.setValue(val);
			awaitRedraw().then(() => {
				this._editLine.focus();
				this.events.fire(SpreadsheetEvents.editLineInput, [val]);
				this._editLine.setCursor(pos);
			});
		} else {
			this._editLine.setCellMath();
			this._editMode = null;
		}
		if (prevPage?.getMeta().filter !== pageMeta.filter) {
			this._filterForm.applyFilter(this._activePage);
		}
		this.events.fire(SpreadsheetEvents.afterSheetChange, [this._activeSheetName]);
		this._grid.paint();
		awaitRedraw().then(() => {
			this._updateGridSizes();
			this._updateGridState();
			if (!this.selection.getSelectedCell() && !this._editMode) {
				this.selection.setSelectedCell("A1");
			}
			focusManager.setFocusId(this._uid);
		});
	}

	private _callAction(config: IExecuteConfig | IExecuteConfig[]) {
		const defaultConf: IExecuteConfig = {
			dataStore: this._dataStore,
			page: this._activePage,
			cell: this.selection.getFocusedCell(),
		};
		if (!config || this.config.readonly) {
			return;
		}
		if (Array.isArray(config)) {
			this._actionsManager.execute(config);
			return;
		}
		let page = this._activePage;
		let cell = config.cell;
		if (cell?.includes("!")) {
			const parse = cell.split("!");
			cell = parse[1];
			page = this._dataStore.getPage(parse[0]);
			if (!page) return;
		}
		config = { ...config, cell, page };
		config.dataStore = this._dataStore;

		if (isRangeId(config.cell as string) && config.groupAction) {
			this._actionsManager.execute(config.groupAction as Actions, { ...defaultConf, ...config });
			return;
		}
		this._actionsManager.execute(config.action as Actions, { ...defaultConf, ...config });
	}

	private _initLayout() {
		this._grid = new Grid(null, {
			rowHeight: 32,
			headerRowHeight: 32,
			headerSort: false,
			tooltip: false,
			leftSplit: 1 + (this.config.leftSplit || 0),
			topSplit: this.config.topSplit || 0,
		});
		this.menu = new Menu(null, { navigationType: "click" });
		this.menu.data.parse(getMenuStruct(this.config) as any);

		this.toolbar = new Toolbar();
		const toolbarStruct = getToolbarStruct(this.config.toolbarBlocks, this.config);
		this.toolbar.data.parse(toolbarStruct);

		this.contextMenu = new ContextMenu();
		this.contextMenu.data.parse(getContextMenuStruct() as any);
		this.sheetContextMenu = new ContextMenu();
		this.sheetContextMenu.data.parse(getSheetsContextMenuStruct(this.config.multiSheets));
		this._tabbar = new Toolbar(null, {
			css: "dhx-sheets-toolbar",
		});

		this._sheets = this._tabbar.data;
		this._sheets.parse([{ value: "sheet1" }]);
		this._dataStore = initMathStore(this._sheets);

		this._math = this._dataStore.getStore();
		this._editLine = new EditLine(null, {
			events: this.events,
			grid: this._grid,
			store: this._activePage,
			spreadsheet: this,
			math: this._math,
		});

		this.config.multiSheets &&
			!this.config.readonly &&
			this._sheets.add({ icon: "dxi dxi-plus", id: "add-sheet" }, 0);

		const layout = (this._layout = new Layout(null, {
			height: "100%",
			rows: [
				{ id: "menu", css: "menu_wrapper", height: "content" },
				{ id: "toolbar", css: "toolbar_wrapper", height: "content" },
				{ id: "editLine", css: "editLine_wrapper", height: "content" },
				{ id: "grid", css: "dhx-spreadsheet-grid dhx_layout-cell--gravity" },
				{ id: "tabbar", css: "sheetbar_wrapper", height: "content" },
			],
		}));

		layout.getCell("menu").attach(this.menu, this);
		layout.getCell("toolbar").attach(this.toolbar, this);
		layout.getCell("grid").attach(this._grid, this);
		layout.getCell("editLine").attach(this._editLine, this);
		layout.getCell("tabbar").attach(this._tabbar, this);

		const initialSheetId = this.config.multiSheets && !this.config.readonly ? 1 : 0;
		const page = this._dataStore.getPage(this._sheets.getItem(this._sheets.getId(initialSheetId)).value);
		page.setMeta({ leftSplit: this.config.leftSplit });

		this._changeSheet(this._sheets.getId(initialSheetId));

		if (!this.config.editLine || this.config.readonly) {
			layout.getCell("editLine").hide();
		}
		if (!this.config.menu || this.config.readonly) {
			layout.getCell("menu").hide();
		}
		if (this.config.readonly || !toolbarStruct || !toolbarStruct.length) {
			layout.getCell("toolbar").hide();
		}
		if (!this.config.multiSheets) {
			layout.getCell("tabbar").hide();
		}
	}

	private _generateGridStruct() {
		const values = this.serialize();
		const { rowsCount, colsCount } = this.config;
		const rows = [];

		for (let i = 1; i <= rowsCount; i++) {
			const row = { id: `${i}`, $index: i };

			for (let k = 1; k <= colsCount; k++) {
				row[k] = "";
			}
			rows.push(row);
		}

		const cols = [
			{
				id: "$index",
				width: 40,
				header: [{ text: "" }],
				htmlEnable: true,
				editable: false,
				align: "right",
				template: (_val, row) => getIndexRowCell(row.$index),
			},
		] as any[];

		let inputVisible = false;
		const blurHandler = (e: FocusEvent) => {
			requestAnimationFrame(() => {
				if (!inputVisible) {
					return;
				}
				setTimeout(() => {
					this._restoreFocus(e);
				});
			});
		};
		const inserted = node => {
			const input: HTMLInputElement = node.el;
			this._range.editor = input;
			this._range.inline = 1;

			input.focus();
			input.setSelectionRange(input.value.length, input.value.length);
			this._range.cursor = input.value.length;

			input.addEventListener("blur", e => {
				this._range.cursor = input.selectionStart;
				blurHandler(e);
			});
			input.addEventListener("focus", () => {
				this._range.inline = 1;
			});

			inputVisible = true;
		};
		const removed = () => {
			this._range.editor = null;
			inputVisible = false;
		};
		const onInput = (cell: string, e) => {
			if (inputVisible) {
				this.events.fire(SpreadsheetEvents.cellInput, [cell, e.target.value]);
			}
		};
		let selected;
		this.events.on(SpreadsheetEvents.gridRedraw, () => {
			selected = null;
		});
		const template = (v, row, col) => {
			if (!selected) {
				selected = this.selection.getFocusedCell();
			}
			const cell = col.$letter + row.$index;
			const cellId = getCellIds(cell, this._grid);
			const meta = this._activePage.getCell(cellId.row, cellId.col) || {};
			const { filter } = this._activePage.getMeta() as IPageMeta;
			let isFilterCell = false;
			let filtered = false;
			if (filter) {
				const { cell: filteredRange, rules } = filter;
				const { start, end } = getCellIds(filteredRange, this._grid);
				if (start.row === cellId.row && cellId.col >= start.col && cellId.col <= end.col) {
					const lastCell = getCellNameById(this._grid, start.row + 1, end.col + 1);
					const cellsArr = getCellsArray(`${filteredRange.split(":")[0]}:${lastCell}`, this._grid);
					isFilterCell = true;
					const { condition, exclude } = rules[cellsArr.indexOf(cell)] || {};
					filtered = !!(condition || exclude);
				}
			}

			const { locked, edited, nextValue, editor, css, format, link } = meta as ICellMeta;
			const val = this._activePage.getValue(cellId.row, cellId.col, format?.mask === "@");
			const cellValue = edited && isDefined(nextValue) ? nextValue : val;

			if (selected && edited) {
				const formatId = format?.id;
				if (formatId === "date" && !this._editors.date) {
					this._editors.date = new DateEditor(
						this.endEdit.bind(this),
						this.config.dateFormat,
						this.events
					);
				}
				if (formatId === "time" && !this._editors.time) {
					this._editors.time = new TimeEditor(
						this.endEdit.bind(this),
						this.config.timeFormat,
						this.events
					);
				}
				if (editor?.type === "select") {
					if (!this._editors.select) {
						this._editors.select = new SelectEditor(this.endEdit.bind(this), this.events);
					}
					const { options } = editor;
					this._editors.select.setValue(
						typeof options === "string" ? this.getValue(options.replace(/=|\$/g, "")) : options
					);
				}
				const conf = {
					oninput: [onInput, cell],
					_hooks: {
						didInsert: inserted.bind(this),
						didRemove: removed.bind(this),
					},
					class: css,
					_key: "selection_input",
					"data-dhx-id": "cell_input",
					value:
						nextValue === undefined || nextValue === null
							? format
								? getFormattedValue(cellValue, format)
								: cellValue
							: nextValue,
				};
				if (this._editors[formatId]) {
					return this._editors[formatId].toHTML({ ...conf, format });
				}
				if (meta.editor?.type === "select") {
					return this._editors.select.toHTML(conf);
				}

				return el("input.dhx_cell_input", conf);
			}
			let typedCellValue: any[] = [];
			const isValid = !editor || this._checkValidation(cell);
			if (link && !link.text) {
				const href = link.href;
				const splited = `${cellValue}`.split(" ");
				splited.forEach(text => {
					if (text === href) {
						typedCellValue.push(
							el(
								"span",
								{
									class: css || "",
								},
								[href]
							)
						);
						typedCellValue.push(" ");
					} else {
						typedCellValue.push(`${text} `);
					}
				});
			} else if (link) {
				const { text, href } = link;
				const val = text || href;
				typedCellValue.push(
					el(
						"span.dhx_spreadsheet_link",
						{
							class: css,
						},
						[format ? getFormattedValue(val, format) : val]
					)
				);
			} else {
				typedCellValue.push(format ? getFormattedValue(cellValue, format) : cellValue);
			}

			return el(
				".dhx_spreadsheet_cell",
				{
					class: css,
				},
				[
					locked && el(".dhx_lock_icon.dxi.dxi-key"),
					...typedCellValue,
					meta.editor?.type === "select"
						? [
								el(".dxi.dxi-menu-down.dhx_spreadsheet_cell_icon.drop-down", {
									onclick: () => this.startEdit(cell),
								}),
								el(".dhx_spreadsheet_cell-placeholder"),
						  ]
						: null,
					isFilterCell && [
						el(
							`.dxi.dxi-filter-variant.dhx_spreadsheet_cell_icon.filter${
								filtered ? "-filtered" : ""
							}`,
							{
								onclick: (e: MouseEvent) => this._showFilter(cell, e),
								ondblclick: (e: MouseEvent) => this._showFilter(cell, e),
							}
						),
						el(".dhx_spreadsheet_cell-placeholder"),
					],
					!isValid && el(".dhx_spreadsheet_cell-not_valid"),
				]
			);
		};
		for (let i = 1; i <= colsCount; i++) {
			const letter = getLetterFromNumber(i);
			cols.push({
				id: `${i}`,
				width: 120,
				$letter: letter,
				type: "string",
				header: [
					{
						text: getHeaderCell(letter, i),
						align: "center",
					},
				],
				htmlEnable: true,
				template,
			});
		}
		this._grid.setColumns(cols);
		this._grid.data.parse(rows);
		this.parse(values);

		this._sizes.rowsCount = this.config.rowsCount;
		this._sizes.colsCount = this.config.colsCount;
	}

	private _checkForMissedCells(cell: string) {
		if (cell.includes("!")) {
			cell = cell.split("!")[1];
		}
		const index = isRangeId(cell)
			? getRangeIndexes(cell, this._grid).end
			: getCellIndex(cell, this._grid);
		if (index.row > this.config.rowsCount || index.col > this.config.colsCount) {
			this.config.rowsCount = Math.max(this.config.rowsCount, index.row + 1);
			this.config.colsCount = Math.max(this.config.colsCount, index.col);
			this._generateGridStruct();
		}
	}
	private _generateNewSheetName(): string {
		let sheetsArr = this._sheets.findAll(i => i.value && i.value.startsWith("sheet"));
		if (!sheetsArr.length) {
			return "sheet1";
		}
		sheetsArr = sheetsArr.map(i => +i.value.split("sheet")[1]).filter(i => !!i);
		return `sheet${Math.max(...sheetsArr) + 1}`;
	}

	private _updateGridSizes() {
		const [pageRows, pageCols] = this._activePage.getSize();
		const gridColumns = this._grid.config.columns as ICol[];
		const gridRowsCount = this._grid.data.getLength();
		const { rowsCount: configRowsCount, colsCount: configColsCount } = this.config;
		const { cols, rows } = this._activePage.getMeta();
		const defaultHeight = this._grid.config.rowHeight;
		while (rows.length < pageRows || rows.length < (configRowsCount as number)) {
			rows.push({ height: defaultHeight });
		}
		if (rows.length > gridRowsCount) {
			let newRows: any[] = [];
			for (let i = gridRowsCount; i < rows.length; i++) {
				const row = { id: `${i + 1}`, $index: i + 1 };
				for (let k = 1; k <= pageCols; k++) {
					row[k] = "";
				}
				if (!this._grid.data.exists(row.id)) {
					newRows.push(row);
				}
			}
			if (newRows.length) {
				const prevData = this._grid.data.serialize().map((r, index) => ({ ...r, $index: index + 1 }));
				this._grid.data.parse([...prevData, ...newRows]);
			}
		}

		while (cols.length < pageCols || cols.length < (configColsCount as number)) {
			cols.push({ width: 120 });
		}

		if (cols.length > gridColumns.length - 1) {
			const lastcol = gridColumns[gridColumns.length - 1];
			for (let i = gridColumns.length - 1; i < cols.length; i++) {
				const letter = getLetterFromNumber(gridColumns.length);
				(gridColumns as any).push({
					id: `${gridColumns.length}`,
					width: 120,
					$letter: letter,
					type: "string",
					header: [
						{
							text: getHeaderCell(letter, gridColumns.length),
							align: "center",
						},
					],
					htmlEnable: true,
					template: lastcol.template,
				});
			}
			this._grid.setColumns(gridColumns);
		}

		if (cols.length < gridColumns.length - 1) {
			while (cols.length !== gridColumns.length - 1) {
				gridColumns.pop();
			}
		}
		if (rows.length < gridRowsCount) {
			while (rows.length !== this._grid.data.getLength()) {
				this._grid.data.remove(this._grid.data.getLength());
			}
		}

		const [firstCol, ...dataCols] = gridColumns;
		dataCols.forEach((col, i) => {
			col.width = cols[i] ? cols[i].width : col.width;
		});
		this._grid.setColumns([firstCol, ...dataCols]);
		this._grid.data.forEach((row, index) => {
			const height = rows[index].height;
			if (row.height !== height) {
				//silence update
				this._grid.data.update(row.id, { height }, true);
			}
			//fire event to real update
			if (index === this._grid.data.getLength() - 1) {
				this._grid.data.events.fire(DataEvents.change, [row.id, "update", row]);
			}
		});
	}
	private _updateGridState() {
		this._grid.config.spans = [];
		const { scroll = { x: 0, y: 0 }, cols, rows, merged = [] } = this._activePage.getMeta();
		const gridColumns = this._grid.config.columns;
		const [firstCol, ...dataCols] = gridColumns;
		const { x, y } = scroll;
		this._grid.scroll(x, y);
		dataCols.forEach((col, i) => {
			col.width = cols[i] ? cols[i].width : col.width;
		});
		this._grid.setColumns([firstCol, ...dataCols]);
		this._grid.data.forEach((row, index) => {
			if (!rows[index]) return;
			const height = rows[index].height;
			if (row.height !== height) {
				this._grid.data.update(row.id, { height });
			}
		});
		this._grid.config.spans = merged.map((span: ISpan) => {
			const { from, to } = span;
			const { row, column } = from;
			const rowspan = +to.row - +row + 1;
			const colspan = +to.column - +column + 1;
			return normalizeSpan(
				{
					row: +row + 1,
					column: +column + 1,
					rowspan: rowspan > 1 ? rowspan : undefined,
					colspan: colspan > 1 ? colspan : undefined,
				},
				{ ...this._grid.config }
			);
		});
		(this.selection as any)._removeHeadersCss();
		(this.selection as any)._setHeadersCss();
	}

	private _updateToolbar(cell: string) {
		if (cell !== this.selection.getFocusedCell()) {
			return;
		}
		updateToolbar(this.toolbar, this.menu, this._activePage, this._grid);
	}

	private _setEventsHandlers() {
		this.events.on(SpreadsheetEvents.editLineInput, val => {
			focusManager.setFocusId(this._uid);
			const cell = this.selection.getFocusedCell();
			this.events.fire(SpreadsheetEvents.cellInput, [cell, val]);
		});
		this.events.on(SpreadsheetEvents.editLineFocus, () => {
			const cell = this.selection.getFocusedCell();
			this.startEdit(cell);
			this._range.inline = 0;

			requestAnimationFrame(() => {
				this._editLine.focus();
			});
		});
		this.events.on(SpreadsheetEvents.cellInput, (cell, val) => {
			if (this.isLocked(cell)) {
				return;
			}
			const { row, col } = getCellIds(cell, this._grid);
			this._activePage.setCell(row, col, {
				nextValue: val,
			});

			this._grid.paint();
		});
		this._grid.events.on(GridEvents.cellMouseDown, () => {
			(this.contextMenu as any)._close();
			(this.sheetContextMenu as any)._close();
		});

		this._grid.events.on(GridEvents.scroll, () => {
			this._linkForm.hideInfo();
		});

		this.events.on(SpreadsheetEvents.afterPopupHide, () => {
			focusManager.setFocusId(this._uid);
		});

		this.events.on(SpreadsheetEvents.afterAction, (action: Actions, conf: IActionConfig) => {
			switch (action) {
				case Actions.resizeRow:
					const gridRows = this._grid.data.map(row => {
						return {
							height: row.$height || this._grid.config.rowHeight,
							changedByUser: row.changedByUser,
						};
					});
					this._activePage.setMeta({ rows: gridRows });
					break;
				case Actions.resizeCol:
					const gridColumns = this._grid.config.columns.slice(1).map(col => {
						return { width: col.width };
					});
					this._activePage.setMeta({ cols: gridColumns });
					break;
				case Actions.insertLink:
					this.selection.setFocusedCell(this.selection.getFocusedCell());
					break;

				case Actions.filter:
				case Actions.merge:
				case Actions.unmerge:
					this._updateToolbar(this.selection.getFocusedCell());
			}
			awaitRedraw().then(() => {
				this.selection.setSelectedCell(this.selection.getSelectedCell());
			});
		});

		this.events.on(SpreadsheetEvents.beforeAction, (actionName: Actions, conf) => {
			switch (actionName) {
				case Actions.sortCells:
					return this._spanControl.checkBeforeAction(conf.cell, this._activePage, locale.sort);
				case Actions.filter:
					return this._spanControl.checkBeforeAction(conf.cell, this._activePage, locale.filter);
				default:
					return true;
			}
		});

		this.events.on(SpreadsheetEvents.beforeSelectionSet, cell => {
			const r = this._range;

			if (r.mode) return true;

			const pos = this._editLine.isCellExpected(cell, false, r.inline ? r.editor || null : null);
			if (pos > 0) {
				r.cursor = pos;
				r.cell = this.selection.getFocusedCell();
				r.mode = 1;
				return true;
			}

			if (this.config.readonly) {
				return false;
			}
			this._linkForm.hide();
			this._linkForm.hideInfo();
			!this._editMode && this.endEdit();
		});
		this.events.on(SpreadsheetEvents.afterSelectionSet, cell => {
			const r = this._range;

			if (!r.mode && this._editMode) {
				this._editLine.setCellMath();
			}
			if (r.mode === 1) {
				r.mode = 2;
				!this._editMode && this.selection.setSelectedCell(r.cell);
				r.mode = 0;
				r.cursor = this._editLine.isCellExpected(cell, true, r.inline ? r.editor : null, r.cell);
				r.cell = "";
			}
			this._grid.paint();

			const { row, col } = getCellIds(cell, this._grid);
			const cellMeta = this._activePage.getCell(row, col) || {};

			const { link } = cellMeta;
			if (link?.href) {
				awaitRedraw().then(() => {
					if (!this._contextMenuVisible && !this._linkForm?.isVisible()) {
						this._linkForm.showInfo(cell, cellMeta);
					}
				});
			}

			if (!this._checkValidation(cell)) {
				if (isRangeId(cell)) return;
				const { row, col } = getCellIds(cell, this._grid);
				const node = this.getRootNode()?.querySelector(
					`[aria-rowindex = "${row + 1}"].dhx_grid-row [aria-colindex = "${col + 2}"]`
				) as HTMLElement;
				new tooltip("Invalid value", {
					node,
					position: Position.right,
					force: true,
					margin: 2,
					css: "dhx_spreadsheet-invalid-tooltip",
				});
			}
		});

		this.events.on(SpreadsheetEvents.afterFocusSet, cell => {
			if (this._range.mode || this._editMode) return;
			const { row, col } = getCellIds(cell, this._grid);
			let val = this._activePage.getValue(row, col, true);
			if (!isNumeric(val) && !val) {
				val = "";
			}
			const info = getCellInfo(cell, this._activePage, this._grid);

			updateToolbar(this.toolbar, this.menu, this._activePage, this._grid);
			this._editLine.setValue(getFormattedValue(val, info.format as IFormats));
			if (info && info.locked) {
				this._editLine.lock();
			}
			this._grid.paint();
		});

		this.events.on(SpreadsheetEvents.afterStyleChange, cell => {
			this._updateToolbar(cell);
		});

		this.events.on(SpreadsheetEvents.afterFormatChange, cell => {
			this._updateToolbar(cell);
		});

		this.events.on(SpreadsheetEvents.afterEditStart, () => {
			this._grid.paint();
		});
		this.events.on(SpreadsheetEvents.afterEditEnd, cell => {
			this._grid.paint();
			let { format } = getCellInfo(cell, this._activePage, this._grid);
			this._editors[format?.id]?.hidePopup();
		});

		this._tabbar.events.on(NavigationBarEvents.click, id => {
			if (id === "add-sheet") {
				this.addSheet();
			} else {
				this._changeSheet(id);
			}
		});
		this.sheetContextMenu.events.on(NavigationBarEvents.click, id => {
			if (id === "delete") {
				this.removeSheet(this._focusedSheet);
			} else {
				const item = this._tabbar.data.getItem(this._focusedSheet);
				this._tabbar.data.update(item.id, { type: "input" });
				awaitRedraw().then(() => {
					const input = this._tabbar.getRootNode().querySelector("input");
					input.focus();
					input.onkeyup = (e: KeyboardEvent) => {
						if (e.key === "Enter") {
							input.blur();
						}
						if (e.key === "Escape") {
							this._sheets.update(item.id, { type: "navItem", value: item.prevValue });
						}
					};
				});
			}
		});
		this.sheetContextMenu.events.on(NavigationBarEvents.afterHide, () => {
			this._focusedSheet = null;
		});

		this._tabbar.events.on(NavigationBarEvents.inputFocus, id => {
			const item = this._sheets.getItem(id);
			this._sheets.update(id, { prevValue: item.value });
		});

		this._tabbar.events.on(NavigationBarEvents.inputBlur, id => {
			const item = this._sheets.getItem(id);
			const newVal = item.value;
			const isExist = this._sheets.find(i => i.value === newVal && i.id !== item.id);
			if (isExist) {
				this._getErrorWindow();
				const [firstPart, lastPart] = locale.renameSheetAlert.split("$name");
				this._errorAlert.attachHTML(`<p>${firstPart}<b>${newVal}</b>${lastPart}</p>`);
				this._errorAlert.show();
				this._errorAlert.footer.events.on(NavigationBarEvents.click, () => this._errorAlert.hide());
				this._errorAlert.events.on(WindowEvents.afterHide, () => {
					this._sheets.update(item.id, { value: item.prevValue });
					const input = this._tabbar.getRootNode().querySelector("input");
					input.focus();
				});

				return false;
			}
			this._sheets.update(id, { type: "navItem", value: item.prevValue });
			this._renameSheet(id, newVal);
		});

		this.toolbar.events.on(NavigationBarEvents.click, (id, e) => {
			if (id === "format" || id === "valign" || id === "halign" || id === "multiline") return;
			this.endEdit();
			this._handleAction(id, e);
		});
		this.contextMenu.events.on(NavigationBarEvents.click, (id, e) => {
			this.endEdit();
			this._handleAction(id, e);
		});
		this.contextMenu.events.on(NavigationBarEvents.afterHide, () => {
			this._contextMenuVisible = false;
			this.contextMenu.hide("fit-col");
			this.contextMenu.show("link");
		});

		this._grid.events.on(GridEvents.headerCellMouseDown, (_col, e) => {
			const sortIcon = (e.target as HTMLElement).className.includes("dhx_header_icon");
			if (sortIcon) {
				this.contextMenu.show("fit-col");
				this.contextMenu.hide("link");
				this.contextMenu.showAt(e);
				return;
			}
		});
		this.menu.events.on(NavigationBarEvents.click, (id, e) => {
			if (id === "format" || id === "valign" || id === "halign") return;
			this.endEdit();
			this._handleAction(id, e);
		});

		this._colorpicker.events.on(ColorpickerEvents.change, color => {
			this._colorpickerPopup.hide();
			const selected = this.selection.getSelectedCell();
			if (!this._silencedColorChange) this.setStyle(selected, { [this._colorpickerTarget]: color });
			this._silencedColorChange = false;
			// restore widget focus
			requestAnimationFrame(() => {
				focusManager.setFocusId(this._uid);
				if (this._activeInput) {
					this._activeInput.focus();
					this._activeInput = null;
				}
			});
		});

		this.events.on(SpreadsheetEvents.editLineBlur, (_val, e) => {
			this._restoreFocus(e);
		});

		this.events.on(SpreadsheetEvents.groupFill, (focused, selected) => {
			this._fillCells(focused, selected);
		});
	}
	private _getErrorWindow() {
		if (!this._errorAlert) {
			this._errorAlert = new Window({
				footer: true,
				modal: true,
				closable: true,
				width: 350,
				height: 240,
				title: locale.alertTitle,
			});

			this._errorAlert.footer.data.parse([
				{
					type: "button",
					view: "flat",
					size: "medium",
					color: "primary",
					value: locale.ok,
					id: "ok",
				},
			]);
		}
		return this._errorAlert;
	}

	private _handleAction(action: Id, e?: any) {
		const selected = this.selection.getSelectedCell();
		const focused = this.selection.getFocusedCell();

		let format: Id = "";
		const formatIndex = findIndex(this.config.formats, f => f.id === action);
		if (formatIndex !== -1) {
			format = action;
			action = "format";
		}
		let align = "";
		let dir = "";
		let wrap = "";
		if (`${action}`.includes("align")) {
			const split = `${action}`.split("-");
			action = split[0];
			align = split[1];
		}
		if (`${action}`.endsWith("sort")) {
			const split = `${action}`.split("-");
			action = split[1];
			dir = split[0];
		}
		if (`${action}`.includes("multiline")) {
			const split = `${action}`.split("-");
			action = split[0];
			wrap = split[1];
		}

		switch (action) {
			case "undo":
				this._actionsManager.undo();
				break;
			case "redo":
				this._actionsManager.redo();
				break;
			case "color":
			case "background": {
				this._colorpickerTarget = action;
				const info = getCellInfo(selected, this._activePage, this._grid);
				const css = getStylesObject(info.css);
				const def = {
					background: "#FFFFFF",
					color: "#4C4C4C",
				};
				const color = css[action] || def[action];
				this._silencedColorChange = true;
				this._colorpicker.setValue(color === "white" ? "#FFFFFF" : rgbToHex(color));
				const btn = locateNode(e, "data-dhx-id") as HTMLElement;
				this._colorpickerPopup.show(btn);
				break;
			}
			case "halign":
				this.setStyle(selected, { "text-align": align });
				break;
			case "valign":
				this.setStyle(selected, { "vertical-align": align });
				break;
			case "multiline":
				this.setStyle(selected, { multiline: wrap });
				break;
			case "font-weight-bold":
			case "font-style-italic":
			case "text-decoration-line-through":
			case "text-decoration-underline": {
				if (!selected) {
					return;
				}
				const idArr = action.includes("line-through")
					? ["text-decoration", "line-through"]
					: action.split("-");
				let val = idArr.pop();
				const key = idArr.join("-");
				const cellArr = selected.split(",");
				for (const cell of cellArr) {
					val = getToggledValue(this._activePage, cell, key, val, this._grid);
					this.setStyle(cell, { ...this.getStyle(cell), [key]: val });
				}
				break;
			}
			case "clear-value":
				this.setValue(selected, "");
				break;
			case "clear-styles":
				this.setStyle(selected, {});
				break;
			case "clear-all":
				if (!selected) {
					return;
				}
				this._callAction([
					{
						cell: selected,
						action: Actions.setCellStyle,
						groupAction: isRangeId(selected) ? Actions.groupAction : null,
						val: "",
						dataStore: this._dataStore,
						page: this._activePage,
					},
					{
						cell: selected,
						action: Actions.setCellValue,
						groupAction: isRangeId(selected) ? Actions.groupAction : null,
						val: "",
						page: this._activePage,
						dataStore: this._dataStore,
					},
					{
						cell: selected,
						action: Actions.setValidation,
						groupAction: isRangeId(selected) ? Actions.groupAction : null,
						val: null,
						page: this._activePage,
						dataStore: this._dataStore,
					},
				]);
				break;
			case "lock":
				if (!selected) {
					return;
				}
				const cellAr = selected.split(",");
				for (const cell of cellAr) {
					if (this.isLocked(cell)) {
						this.unlock(cell);
					} else {
						this.lock(cell);
					}
				}
				break;
			case "remove-row":
				this.deleteRow(focused);
				break;
			case "add-row":
				this.addRow(focused);
				break;
			case "remove-col":
				this.deleteColumn(focused);
				break;
			case "add-col":
				this.addColumn(focused);
				break;
			case "fit-col":
				this.fitColumn(selected.split(":")[0]);
				break;
			case "help":
				window.open("https://docs.dhtmlx.com/spreadsheet/user_guide.html");
				break;
			case "format":
				this.setFormat(selected, format);
				this.paint();
				break;
			case "export-xlsx":
				this.export.xlsx();
				break;
			case "import-xlsx":
				this.load("", "xlsx");
				break;
			case "validation":
				if (!this._validationForm) {
					this._validationForm = new ValidationForm(
						this.events,
						this.setValidation.bind(this),
						this._grid
					);
				}
				this._validationForm.show(selected, getCellInfo(selected, this._activePage, this._grid));
				break;
			case "sort":
				this.sortCells(selected, dir === "asc" ? 1 : -1);
				break;
			case "search":
				this.search("", true);
				break;
			case "link":
				this._contextMenuVisible = false;
				this._showLinkPopup(focused);
				break;
			case "filter":
				const { filter } = this._activePage.getMeta();
				this.setFilter(selected, filter ? null : []);
				break;
			case "merge":
				this.mergeCells(selected, this.toolbar.data.getItem("merge").active);
				break;
			default:
				break;
		}
	}

	private _parseCell(cell: string): { cleanCell: string; page: DataPage } {
		let page = this._activePage;
		let cleanCell = cell;
		if (cell.includes("!")) {
			const parse = cell.split("!");
			cleanCell = parse[1];
			page = this._dataStore.getPage(parse[0]);
		}
		return { cleanCell, page };
	}

	private _fillCells(focused: string, selected: string) {
		let focusedCells = [focused];
		let selectedCells = [selected];

		if (isRangeId(focused)) {
			const largerByX = getCellsDiff(focused.split(":")[1], selected.split(":")[1], this._grid)
				.isLargerByX;

			const dir = largerByX ? "row" : "col";

			const matrix = getRangeMatrix(focused, this._grid, dir);
			focusedCells = matrix.map(row => {
				return row.length > 1 ? row[0] + ":" + row[row.length - 1] : row[0];
			});
			selectedCells = getRangeMatrix(selected, this._grid, dir).map(row => {
				return row.length > 1 ? row[0] + ":" + row[row.length - 1] : row[0];
			});
		}
		const { merged } = this._activePage.getMeta();
		const spans: string[] = [];
		const unmerge: any[] = [];
		const actions = focusedCells.reduce((acc, row, i) => {
			let cells = selectedCells[i];
			let value;
			if (isRangeId(row)) {
				value = [];
				this.eachCell(cell => {
					const { row, col } = getCellIds(cell, this._grid);
					if (this._grid?.isRowHidden(row + 1)) return;
					const meta = { ...this._activePage.getCell(row, col) };
					const cellVal = this._activePage.getValue(row, col, true);
					meta.value = !isDefined(cellVal) || doesContainUrl(cellVal) ? "" : cellVal;
					const span = isCellSpaned(cell, merged, this._grid);
					if (span && span.from.row === row && span.from.column === col) {
						meta.span = isCellSpaned(cell, merged, this._grid);
					}
					value.push(meta);
				}, row);
			} else {
				const cell = row;
				const ids = getCellIds(cell, this._grid);
				const meta = { ...this._activePage.getCell(ids.row, ids.col) };
				const val = this._activePage.getValue(ids.row, ids.col, true);
				meta.value = getExcelDateFromString(val as string, meta.format) || "";
				const format = meta.format?.id;
				const span = isCellSpaned(cell, merged, this._grid);
				value =
					format === "date" || format === "time" || span ? [meta] : doesContainUrl(val) ? "" : val;
				if (span && span.from.row === ids.row && span.from.column === ids.col) {
					meta.span = isCellSpaned(cell, merged, this._grid);
				}
			}

			if (Array.isArray(value)) {
				if (row === cells) {
					return acc;
				}
				const filled = getAutoFilledCells(value, row, cells, this._math, this._grid);
				({ cells, value } = filled);
				spans.push(...filled.spans);
			}

			const styles = this.getStyle(row);
			const format = this.getFormat(row);
			let editors = [];
			let links = [];
			this.eachCell(cell => {
				const { editor, link } = getCellInfo(cell, this._activePage, this._grid);
				editors.push(editor?.options);
				links.push(link || null);
			}, row);

			this.eachCell(cell => {
				// unmerge cells
				unmerge.push({
					cell,
					action: Actions.unmerge,
					groupAction: Actions.unmerge,
					page: this._activePage,
				});
			}, cells);
			acc.push(
				{
					cell: cells,
					action: Actions.setCellFormat,
					groupAction: isRangeId(cells) ? Actions.groupAction : null,
					val: format,
					page: this._activePage,
				},
				{
					cell: cells,
					action: Actions.setCellStyle,
					groupAction: isRangeId(cells) ? Actions.groupAction : null,
					val: styles,
					page: this._activePage,
					replace: true,
				},
				{
					cell: cells,
					action: Actions.setValidation,
					groupAction: Actions.groupAction,
					val: editors,
					page: this._activePage,
				}
			);

			if (typeof value === "string" && value.startsWith("=")) {
				const firstCell = getCellIds(row, this._grid);
				this.eachCell(cell => {
					const targetCell = getCellIds(cell, this._grid);
					const math = this._math.transposeMath(
						value,
						targetCell.row - firstCell.row,
						targetCell.col - firstCell.col
					);
					acc.push({
						cell,
						action: Actions.setCellValue,
						groupAction: null,
						val: math,
						page: this._activePage,
					});
				}, cells);
			} else {
				acc.push({
					cell: cells,
					action: Actions.setCellValue,
					groupAction: isRangeId(cells) ? Actions.groupAction : null,
					val: value,
					page: this._activePage,
				});
			}
			acc.push({
				cell: cells,
				action: Actions.insertLink,
				groupAction: Actions.groupAction,
				val: links,
				page: this._activePage,
			});

			return acc;
		}, []);
		const merge: any[] = [];
		spans.forEach(c => {
			//merge cells
			merge.push({
				cell: c,
				action: Actions.merge,
				groupAction: Actions.merge,
				page: this._activePage,
				$autofill: true,
			});
		});
		this._callAction([...unmerge, ...actions, ...merge]);
	}

	private _restoreFocus(e: FocusEvent) {
		const editLineRoot = this._editLine.getRootView().node;
		const editLineContainer = editLineRoot && editLineRoot.el;

		const focusedCell = this._range.cell || this.selection.getFocusedCell();
		const { col } = getCellIds(focusedCell, this._grid);
		const isFixed = col <= this.config.leftSplit;

		if (focusedCell && (!getCellInfo(focusedCell, this._activePage, this._grid).edited || isFixed)) {
			return;
		}
		const focusID = focusManager.getFocusId();

		if (
			focusID !== this._uid &&
			focusID !== (this._grid as any)._uid &&
			(this._editLine as any)._suggest?.isVisible() &&
			focusID !== (this._editLine as any)._suggest._list?._uid &&
			focusID !== (this._tabbar as any)._uid
		) {
			if (this._colorpickerTarget) {
				this._activeInput = e.target as HTMLInputElement;
				return;
			}
			this.endEdit();
			return;
		}

		const target = e.target as HTMLElement;
		const relatedTarget = e.relatedTarget as HTMLElement;

		if (editLineContainer && editLineContainer.contains(relatedTarget)) {
			return;
		}
		if (
			editLineContainer &&
			editLineContainer.contains(target) &&
			relatedTarget &&
			relatedTarget.getAttribute("data-dhx-id") === "cell_input"
		) {
			return;
		}
		if (
			relatedTarget?.getAttribute("data-dhx-id") === "cell_input" &&
			target.getAttribute("data-dhx-id") === "cell_input"
		)
			return;
		const input = target as HTMLInputElement;
		input.focus();

		// [dirty]
		setTimeout(() => {
			const cursor = this._range.cursor || input.value.length;
			input.selectionStart > cursor && input.setSelectionRange(cursor, cursor);
		}, 100);
	}

	private _initHotkeys() {
		if (this.config.readonly) return;
		this.keyManager = new KeyManager(
			(e: KeyboardEvent, focusId: string) =>
				focusId === this._uid ||
				focusId === (this._grid as any)._uid ||
				focusId === (this.toolbar as any)._uid
		);
		const handlers = getKeysHandlers(this, this._grid, this._buffer, this._editLine);

		for (const key in handlers) {
			this.keyManager.addHotKey(key, handlers[key]);
		}

		for (const key in this.config.hotkeys) {
			this.keyManager.addHotKey(key, this.config.hotkeys[key]);
		}
	}

	private _render() {
		return el(
			".dhx_widget.dhx-spreadsheet",
			{
				dhx_widget_id: this._uid,
				_key: this._uid,
			},
			[inject(this._layout.getRootView())]
		);
	}
}
